"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.getIconSize = exports.defaultProps = exports.default = exports.Button = void 0;
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _conditionalAttributes = require("../../util/conditionalAttributes/conditionalAttributes");
var _Icon = _interopRequireDefault(require("../Icon/Icon"));
var _Text = _interopRequireDefault(require("../Text/Text"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * Button Component
 * @method
 * @param {ButtonProps} props - React properties passed from composition
 * @returns Button
 */
/**
 * Primary UI component for user interaction
 */
const Button = props => {
  const {
    ariaLabel,
    buttonRef = defaultProps.buttonRef,
    children,
    className,
    disabled = defaultProps.disabled,
    fullyClickableParent,
    iconName = defaultProps.iconName,
    iconOnly = defaultProps.iconOnly,
    iconPosition = defaultProps.iconPosition,
    iconSize,
    isKnockoutLink = defaultProps.isKnockoutLink,
    label = defaultProps.label,
    onClick = defaultProps.onClick,
    screenReaderText = defaultProps.screenReaderText,
    size = defaultProps.size,
    tabIndex,
    type = defaultProps.type,
    variant = defaultProps.variant,
    width = defaultProps.width,
    ...other
  } = props;
  const isIconOnly = iconOnly && !!iconName;
  const iconClassName = (0, _classnames.default)('pal-c-Button__icon', {
    [`pal-c-Button__icon--${iconPosition}`]: iconPosition
  });
  const isArrow = variant === 'linkArrow';
  const iconPresentBefore = iconName && iconPosition === 'before';
  const iconPresentAfter = iconName && iconPosition === 'after';
  const isLinkStyle = variant === 'link' || variant === 'linkSecondary' || isArrow;
  const derivedIconSize = getIconSize({
    iconOnly,
    iconSize,
    variant
  });
  const isNavigation = variant === 'navigation';
  const isFullWidth = width === 'fullWidth' && !iconOnly && !isLinkStyle;
  const isFullWidthMobile = width === 'fullWidthMobile' && !iconOnly && !isLinkStyle;
  const componentClassName = (0, _classnames.default)('pal-c-Button', {
    [`pal-c-Button--${variant}`]: variant,
    'pal-c-Button--knockout': isKnockoutLink === true,
    [`pal-c-Button--${size}`]: size,
    'pal-c-Button--disabled': disabled === true,
    'pal-c-Button--iconOnly': isIconOnly,
    'pal-c-Button--fullWidth': isFullWidth,
    'pal-c-Button--fullWidthMobile': isFullWidthMobile,
    'pal-c-Button--absolute': fullyClickableParent,
    [`${className}`]: className
  });
  const computedIcon = isArrow ? /*#__PURE__*/_react.default.createElement(_Icon.default, {
    "aria-hidden": "true",
    focusable: false,
    name: "arrowForward",
    size: derivedIconSize
  }) : iconName && /*#__PURE__*/_react.default.createElement(_Icon.default, {
    "aria-hidden": "true",
    name: iconName,
    size: derivedIconSize,
    className: iconClassName,
    disabled: disabled
  });
  const conditionalProps = (0, _conditionalAttributes.conditionalAttributes)({
    tabIndex: Number.isInteger(tabIndex) ? tabIndex : undefined,
    'aria-label': ariaLabel || undefined
  });
  const buttonProps = {
    className: componentClassName,
    disabled,
    ref: buttonRef,
    type,
    onClick,
    ...conditionalProps,
    ...other
  };
  if (fullyClickableParent) {
    return /*#__PURE__*/_react.default.createElement("button", buttonProps);
  }
  return /*#__PURE__*/_react.default.createElement("button", buttonProps, iconPresentBefore && computedIcon, label && !iconOnly && variant !== 'toggle' && !isNavigation && /*#__PURE__*/_react.default.createElement("span", {
    className: "pal-c-Button__text"
  }, label), children, (iconPresentAfter || isArrow) && computedIcon, screenReaderText && /*#__PURE__*/_react.default.createElement(_Text.default, {
    htmlTag: "span",
    screenReader: true
  }, screenReaderText));
};

/**
 * Gets icon size based on if the icon size prop is passed or set the icon size based on the variant
 * @method
 * @param {Object} props - React properties passed from composition
 * @returns {string} iconSize
 */
exports.Button = Button;
const getIconSize = data => {
  const {
    iconOnly,
    iconSize,
    variant
  } = data;
  if (iconSize) {
    return iconSize;
  }
  if (variant === 'navigation' || variant === 'linkArrow' || iconOnly || variant === 'toggle') {
    return 'lg';
  } else {
    return 'default';
  }
};
/**
 * Property type definitions
 * @type {object}
 * @property {any} buttonRef - Set the default string value for buttonRef
 * @property {boolean} disabled - Disables the field and prevents editing the contents
 * @property {oneOf} width - Sets the width of button (default, fullWidth, fullWidthMobile)
 * @property {string} iconName - Name of SVG icon (i.e. caretDown, minus, warning)
 * @property {boolean} iconOnly - This removes the text to make it an icon only
 * @property {string} iconPosition - This sets the icon to the left or right of the button
 * @property {bool} isKnockoutLink - Knockout styling for an inverted link
 * @property {oneOfType} label - The visible button text
 * @property {func} onClick - On click handler for component
 * @property {string} screenReaderText - Visually hidden additional instruction text to help provide screen reader users additional context.
 * @property {string} size - Available size variations for the button (default, sm, xs)
 * @property {string} type - Determines type of button (button, reset, submit)
 * @property {string} variant - Available _stylistic_ variations available for the Button component (primary, secondary, tertiary, link, linkSecondary)
 */
exports.getIconSize = getIconSize;
const propTypes = exports.propTypes = {
  /**
   * Button reference
   */
  buttonRef: _propTypes.default.any,
  /**
   * Disables the field and prevents editing the contents
   */
  disabled: _propTypes.default.bool,
  /**
   * Sets the width of the button
   * default - current default button width
   * fullWidth - 100% width on all viewports
   * fullWidthMobile - 100% width on mobile and tablet only, default width on desktop
   */
  width: _propTypes.default.oneOf(['default', 'fullWidth', 'fullWidthMobile']),
  /**
   * Name of SVG icon (i.e. caretDown, minus, warning)
   */
  iconName: _propTypes.default.string,
  /**
   * This removes the text to make it an icon only
   */
  iconOnly: _propTypes.default.bool,
  /**
   * Placement of icon relative to text
   */
  iconPosition: _propTypes.default.oneOf(['before', 'after']),
  /**
   * Knockout variant for component rendered on a dark background
   */
  isKnockoutLink: _propTypes.default.bool,
  /**
   * The visible button text
   */
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.node]),
  /**
   * On click handler for component
   */
  onClick: _propTypes.default.func,
  /**
   * Visually hidden additional instruction text to help provide screen reader users additional context. For instance, "View details" might be the visible button text, but screenReaderText might add additional instructions such as "for confirmation number C1234567"
   */
  screenReaderText: _propTypes.default.string,
  /**
   * Determines size of button icon
   * - **default:** Sets the default icon size
   * - **lg:** Sets the large icon size
   * - **xl:** Sets the extra large icon size
   */
  iconSize: _propTypes.default.oneOf(['default', 'lg', 'xl']),
  /**
   * Determines size of button
   * - **default:** Sets the default button size
   * - **compact:** Sets the compact button size
   * - **tiny:** Sets the tiny button size
   */
  size: _propTypes.default.oneOf(['default', 'compact', 'tiny']),
  /**
   * Determines type of button
   * - **button:** The button is a clickable button.
   * - **submit:** The button is a submit button (submits form data). This is the default `button` behavior
   * - **reset:** The button is a reset button (resets the form-data to its initial values)
   */
  type: _propTypes.default.oneOf(['button', 'reset', 'submit']),
  /**
   * Determines variation of button
   * - **primary:** The primary button style
   * - **primaryInverse:** The primary inverse button style
   * - **secondary:** The secondary button style
   * - **tertiary:** The tertiary button style
   * - **link:** The button as a link style
   * - **linkSecondary:** The button as a link secondary style
   * - **navigation:** The navigation buttons for a rail
   * - **toggle:** The toggle buttons to be used on PDP and in Product Cards
   * - **ai:** The AI button style with gradient border
   */
  variant: _propTypes.default.oneOf(['primary', 'primaryInverse', 'secondary', 'tertiary', 'link', 'linkSecondary', 'linkTertiary', 'linkArrow', 'navigation', 'toggle', 'unstyled', 'ai'])
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {any} buttonRef - Set the default string value for buttonRef
 * @property {boolean} disabled - Set the default string value for disabled
 * @property {oneOf} width - Set the default width for button
 * @property {oneOf} iconName - Set the default string value for iconName
 * @property {boolean} iconOnly=false - Default iconOnly
 * @property {boolean} iconPosition=before - Default iconPosition
 * @property {bool} isKnockoutLink - set the default value of knockout
 * @property {oneOfType} label - Set the default string value for text
 * @property {func} onClick - Set the default string value for onClick
 * @property {string} screenReaderText- Set the default string value for screenReaderText
 * @property {oneOf} size - Set the default string value for size
 * @property {oneOf} type - Set the default string value for type
 * @property {oneOf} variant - Set the default string value for variant
 */

const defaultProps = exports.defaultProps = {
  buttonRef: null,
  disabled: false,
  width: 'default',
  iconName: '',
  iconOnly: false,
  iconPosition: 'before',
  isKnockoutLink: false,
  label: '',
  onClick: null,
  screenReaderText: '',
  size: 'default',
  type: 'button',
  variant: 'primary'
};
Button.propTypes = propTypes;
var _default = exports.default = Button;